import React, { useMemo, useState } from "react";
import CustomTable from "components/Table/CustomTable";
import { useModal } from "contexts/ModalProvider";
import { formatCurrency } from "utils/formatters";
import { ProductRecomSchema } from "model/contents/prod-recom.schema";
import useTableChangeHandler from "hooks/useTableChangeHandler";
import { useToasts } from "utils/useHotToast";
import ToastNotification from "components/Toast/ToastNotification";
import check from "assets/icons/check-circle.svg";
import slash from "assets/icons/slash-circle.svg";
import { useAddProdRecomToHighlightMutation } from "services/contents/api";
import dayjs from "dayjs";
import Image from "components/Common/Image";
import { getImageUrl, ImageSourceType } from "utils/imageUtils";

const ProductRecomNomaroiTable = ({ data, error, isLoading, onTableChange }) => {
  const { modal } = useModal();
  const { addToast, removeToast } = useToasts();
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [addProdRecomToHighlight, { isLoading: isLoadingAdd }] = useAddProdRecomToHighlightMutation();

  const { handleSort, handleFilter, handleSearch, handlePageChange } = useTableChangeHandler(onTableChange);

  const columns = useMemo(
    () => [
      {
        header: "รายการ Product Affiliate",
        accessor: "name",
        sortable: true,
        render: (item) => (
          <div className="product-info d-flex align-items-center gap-3">
            <Image src={getImageUrl(item.imgThumbnail, { type: ImageSourceType.NOMAROI })} alt={item.name} />
            <div>
              <div className="text-lg fw-semibold">{item.name}</div>
              <div className="text-md text-gray-500">SKU: {item.sku}</div>
            </div>
          </div>
        ),
      },
      {
        header: "แบรนด์ F&N",
        accessor: "brand",
        responsive: "md",
        render: (item) => (
          <div className="brand-info d-flex align-items-center gap-3">
            <Image src={getImageUrl(item.brand?.imgThumbnail, { type: ImageSourceType.NOMAROI })} alt={item.brand?.name} />
            <div className="text-lg">{item.brand?.name}</div>
          </div>
        ),
      },
      {
        header: "หมวดหมู่สินค้า",
        accessor: "category",
        responsive: "lg",
        render: (item) => <div className="text-success-500 fw-semibold">{item.category?.name}</div>,
      },
      {
        header: "ราคา",
        accessor: "pricePromotion",
        sortable: true,
        align: "end",
        render: (item) => formatCurrency(item.pricePromotion ? item.pricePromotion : item.price),
      },
      {
        header: "วันที่สร้างสินค้า",
        accessor: "createdDate",
        responsive: "xl",
        render: (item) => dayjs(item.createdAt).format("YYYY-MM-DD"),
      },
      {
        header: "",
        accessor: "actions",
        width: 50,
        render: (item) => (
          <div
            className="text-gray-500 cursor-pointer"
            onClick={(event) => {
              event.stopPropagation();
              handleAddNomaroi(item);
            }}
          >
            <i className="bx bx-plus"></i>
          </div>
        ),
      },
    ],
    []
  );

  const handleAddNomaroi = async (item) => {
    const result = await modal.alert.primary({
      title: "ยืนยันที่จะเพิ่มสินค้าไปยังสินค้าที่แนะนำ",
      message: `ตรวจสอบรายละเอียดสินค้าที่คุณต้องการเพิ่มเข้าไปยังสินค้าที่แนะนำว่าถูกต้องหรือไม่?`,
      confirmText: "ยืนยันที่จะเพิ่ม",
      cancelText: "ยกเลิก",
      children: (
        <div className="product-info d-flex align-items-center gap-3">
          <Image src={getImageUrl(item.imgThumbnail, { type: ImageSourceType.NOMAROI })} alt={item.name} />
          <div>
            <div className="text-lg fw-semibold">{item.name}</div>
            <div className="text-md text-gray-500">SKU: {item.sku}</div>
          </div>
        </div>
      ),
    });
    if (result) {
      try {
        await addProdRecomToHighlight(item);
        const toastId = addToast(<ToastNotification variant="success" icon={check} title="เพิ่มสินค้าไปยังสินค้าที่แนะนำแล้ว" description={`สินค้า "${item.name}" ถูกเพิ่มไปยังสินค้าที่แนะนำเรียบร้อยแล้ว`} onSecondaryClick={() => removeToast(toastId)} />, {
          autoDismiss: true,
        });
        // Refresh data
        if (onTableChange) {
          onTableChange({});
        }
      } catch (error) {
        console.error("Failed to add product to highlight:", error);
        const toastId = addToast(<ToastNotification variant="danger" icon={slash} title="เกิดข้อผิดพลาด" description="ไม่สามารถเพิ่มสินค้าไปยังสินค้าที่แนะนำได้ กรุณาลองใหม่อีกครั้ง" onSecondaryClick={() => removeToast(toastId)} />, {
          autoDismiss: true,
        });
      }
    }
  };

  const handleAddSelected = async (items) => {
    const result = await modal.alert.primary({
      title: "ยืนยันที่จะเพิ่มสินค้าไปยังสินค้าที่แนะนำ",
      message: `คุณได้ทำการกดเพิ่มสินค้าไปยังสินค้าที่แนะนำในระบบทั้งหมด "${items.length} รายการ"`,
      confirmText: "ยืนยันที่จะเพิ่ม",
      cancelText: "ยกเลิก",
      children: (
        <div className="d-flex flex-column gap-3 divide-y">
          {items.map((item, index) => (
            <div key={item.id} className={`d-flex flex-column gap-2 ${index > 0 && "pt-3"}`}>
              <div className="product-info d-flex align-items-center gap-3">
                <Image src={getImageUrl(item.imgThumbnail, { type: ImageSourceType.NOMAROI })} alt={item.name} />
                <div>
                  <div className="text-lg fw-semibold">{item.name}</div>
                  <div className="text-md text-gray-500">SKU: {item.sku}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ),
    });
    if (result) {
      try {
        await addProdRecomToHighlight(items);
        const toastId = addToast(<ToastNotification variant="success" icon={check} title={`เพิ่มสินค้าไปยังสินค้าที่แนะนำแล้ว ${selectedProducts.length} รายการ`} description="สินค้าที่เลือกถูกเพิ่มไปยังสินค้าที่แนะนำเรียบร้อยแล้ว" onSecondaryClick={() => removeToast(toastId)} />, {
          autoDismiss: true,
        });
        // Refresh data
        if (onTableChange) {
          onTableChange({});
        }
        // Clear selection
        setSelectedProducts([]);
      } catch (error) {
        const toastId = addToast(<ToastNotification variant="danger" icon={slash} title="เกิดข้อผิดพลาด" description="ไม่สามารถเพิ่มสินค้าไปยังสินค้าที่แนะนำได้ กรุณาลองใหม่อีกครั้ง" onSecondaryClick={() => removeToast(toastId)} />, {
          autoDismiss: true,
        });
      }
    }
  };

  const handleSelectionChange = (selected) => {
    setSelectedProducts(selected);
  };

  return (
    <div className="mt-4">
      <CustomTable
        keyField="id"
        data={data?.data || []}
        columns={columns}
        selectable={true}
        onSelectionChange={handleSelectionChange}
        selectionActions={
          <a
            className="success"
            onClick={() => {
              if (selectedProducts.length > 0) {
                handleAddSelected(selectedProducts);
              }
            }}
          >
            <i className="bx bx-plus" />
            <span className="d-none d-md-block">เพิ่มรายการที่เลือกไว้</span>
          </a>
        }
        itemsPerPage={data?.pagination?.limit || 10}
        totalItems={data?.pagination?.total || 0}
        onSort={handleSort}
        onFilter={handleFilter}
        onSearch={handleSearch}
        onPageChange={handlePageChange}
        status={isLoading ? "loading" : error ? "error" : data?.data?.length === 0 ? "empty" : "normal"}
        errorMessage={error?.message || "เกิดข้อผิดพลาดในการโหลดข้อมูล"}
        emptyMessage="ไม่พบข้อมูลสินค้า Nomaroi"
      />
    </div>
  );
};

const withYupValidation = (WrappedComponent, schema) => {
  return function WithYupValidation(props) {
    // Skip validation if loading or error
    if (props.isLoading || props.error) {
      return <WrappedComponent {...props} />;
    }

    try {
      schema.validateSync(props.data);
      return <WrappedComponent {...props} />;
    } catch (error) {
      return (
        <div className="alert alert-danger">
          <strong>Validation Error:</strong> {error.message}
        </div>
      );
    }
  };
};

const ValidatedTable = withYupValidation(ProductRecomNomaroiTable, ProductRecomSchema);

export default ValidatedTable;
